import PhoneFormikInput from '@/components/PhoneFormikInput/PhoneFormikInput';
import { useAppContext } from '@/contexts/AppContext';
import { LeadStatus, type Lead } from '@/domain/leads';
import type { Unit } from '@/domain/units';
import { LeadService } from '@/services/leads';
import { handleError } from '@/utils/errors';
import { normalizePhoneNumber, phoneValidator } from '@/utils/phone';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useCallback, useState, type FC } from 'react';
import * as Yup from 'yup';

interface TourFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
}

const validationSchema = Yup.object({
  firstName: Yup.string()
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be less than 50 characters')
    .required('First name is required'),
  lastName: Yup.string()
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be less than 50 characters')
    .required('Last name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  phone: Yup.string().test('is-phone', 'You must enter a valid phone number', (value) => {
    if (!value) {
      return true;
    }

    return phoneValidator(value);
  }),
  message: Yup.string().max(500, 'Message must be less than 500 characters'),
});

const initialValues: TourFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  message: '',
};

interface Props {
  onSuccess?: () => void;
  getRedirectTo?: (unit: Unit, lead: Lead) => string;
  submitButtonText: string;
  className?: string;
  unit: Unit;
  showDisclaimer?: boolean;
}

const ScheduleTourForm: FC<Props> = ({
  onSuccess,
  getRedirectTo,
  submitButtonText,
  className = 'space-y-6',
  unit,
  showDisclaimer,
}) => {
  const { community, tenantClientUrl } = useAppContext();

  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (values: TourFormValues) => {
      if (!unit) {
        return;
      }

      try {
        setLoading(true);
        const lead = await LeadService.create(community.id, {
          source: 'btrClient',
          status: LeadStatus.ACTIVE,
          firstName: values.firstName,
          lastName: values.lastName,
          phone: normalizePhoneNumber(values.phone),
          email: values.email,
          message: values.message || undefined,
          propertyId: unit.property.id,
          floorplanId: unit.floorPlanId,
          unitId: unit.id,
        });

        onSuccess?.();

        if (!!getRedirectTo) {
          const target = getRedirectTo(unit, lead);
          location.href = /^https?:\/\//i.test(target) ? target : `${tenantClientUrl}${target}`;
        }
      } catch (error) {
        handleError(error, { toastFallbackMessage: 'An error occurred, please try again' });
      } finally {
        setLoading(false);
      }
    },
    [unit, community.id, onSuccess, getRedirectTo, tenantClientUrl],
  );

  if (!unit) {
    return null;
  }

  return (
    <Formik<TourFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched }) => (
        <Form className={className}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                First Name *
              </label>
              <Field
                type="text"
                id="firstName"
                name="firstName"
                className={`w-full px-4 py-2 border rounded-none focus:ring-2 focus:ring-primary-600 focus:border-transparent ${
                  errors.firstName && touched.firstName ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <ErrorMessage
                name="firstName"
                component="div"
                className="text-red-500 text-sm mt-1"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                Last Name *
              </label>
              <Field
                type="text"
                id="lastName"
                name="lastName"
                className={`w-full px-4 py-2 border rounded-none focus:ring-2 focus:ring-primary-600 focus:border-transparent ${
                  errors.lastName && touched.lastName ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <ErrorMessage name="lastName" component="div" className="text-red-500 text-sm mt-1" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <Field
                type="email"
                id="email"
                name="email"
                className={`w-full px-4 py-2 border rounded-none focus:ring-2 focus:ring-primary-600 focus:border-transparent ${
                  errors.email && touched.email ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone *
              </label>
              <Field
                type="tel"
                id="phone"
                name="phone"
                className={`w-full px-4 py-2 border rounded-none focus:ring-2 focus:ring-primary-600 focus:border-transparent ${
                  errors.phone && touched.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                component={PhoneFormikInput}
              />
              <ErrorMessage name="phone" component="div" className="text-red-500 text-sm mt-1" />
            </div>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
              Additional Message
            </label>
            <Field
              as="textarea"
              id="message"
              name="message"
              rows={4}
              className={`w-full px-4 py-2 border rounded-none focus:ring-2 focus:ring-primary-600 focus:border-transparent resize-none ${
                errors.message && touched.message ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Let us know if you have any questions or special requests..."
            />
            <ErrorMessage name="message" component="div" className="text-red-500 text-sm mt-1" />
          </div>

          <button type="submit" className="w-full btn-primary text-lg" disabled={loading}>
            {submitButtonText}
          </button>

          {showDisclaimer ? (
            <p className="text-xs text-gray-500 text-center">
              * Required fields. By submitting this form, you agree to be contacted by our team.
            </p>
          ) : null}
        </Form>
      )}
    </Formik>
  );
};

export default ScheduleTourForm;
